import React, { useState, useMemo } from 'react';
import {
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  ListItemText,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  createNotice,
  type CreateNoticeRequest,
} from '../../endpoints/notices';
import { getGroupTypes } from '../../endpoints/groups';
import type { GroupType } from '../../types/group';
import AlertItem from '../../Components/AlertItem';

const SCOPES = ['international', 'spd_chapter', 'local_chapter'] as const;

const NoticeManagementCreate: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [recipientGroupTypeIds, setRecipientGroupTypeIds] = useState<string[]>(
    []
  );
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);

  const { data: groupTypesByScope, isLoading: loadingGroupTypes } = useApiGet<
    GroupType[]
  >(
    ['group-types-notices', ...SCOPES],
    async () => {
      const results = await Promise.all(
        SCOPES.map((scope) => getGroupTypes(scope))
      );
      const seen = new Set<string>();
      const merged: GroupType[] = [];
      results.flat().forEach((gt) => {
        if (!seen.has(gt.id)) {
          seen.add(gt.id);
          merged.push(gt);
        }
      });
      return merged;
    },
    { enabled: true }
  );

  const groupTypeOptions = useMemo(
    () => groupTypesByScope ?? [],
    [groupTypesByScope]
  );

  const { mutate: createMutate, isPending: isSaving } = useApiSend(
    (body: CreateNoticeRequest) => createNotice(body),
    () => {
      setAlertState({
        severity: 'success',
        description: 'Notice created. Recipients will receive it shortly.',
      });
      navigate('/administration/notice-management');
    },
    (err: Error) => {
      setAlertState({
        severity: 'error',
        description:
          (err as any)?.response?.data?.error ||
          (err as any)?.message ||
          'Create failed.',
      });
    },
    [['notices']]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setAlertState({ severity: 'error', description: 'Title is required.' });
      return;
    }
    if (!description.trim()) {
      setAlertState({
        severity: 'error',
        description: 'Description is required.',
      });
      return;
    }
    if (recipientGroupTypeIds.length === 0) {
      setAlertState({
        severity: 'error',
        description: 'Select at least one recipient group type.',
      });
      return;
    }
    createMutate({
      title: title.trim(),
      description: description.trim(),
      link_url: linkUrl.trim() || undefined,
      expiry_date: expiryDate || new Date().toISOString().slice(0, 10),
      recipient_group_type_ids: recipientGroupTypeIds,
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Create Notice
        </Typography>
      </Grid>
      {alertState && (
        <Grid item xs={12}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Link URL (optional)"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  size="small"
                  placeholder="https://..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expiry date"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Recipients</InputLabel>
                  <Select
                    multiple
                    value={recipientGroupTypeIds}
                    onChange={(e) =>
                      setRecipientGroupTypeIds(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value
                      )
                    }
                    input={<OutlinedInput label="Recipients" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const gt = groupTypeOptions.find((g) => g.id === id);
                          return (
                            <Chip
                              key={id}
                              size="small"
                              label={gt?.name ?? id}
                            />
                          );
                        })}
                      </Box>
                    )}
                    disabled={loadingGroupTypes}
                  >
                    {groupTypeOptions.map((gt) => (
                      <MenuItem key={gt.id} value={gt.id}>
                        <ListItemText primary={gt.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() =>
                      navigate('/administration/notice-management')
                    }
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSaving || loadingGroupTypes}
                    startIcon={isSaving ? <CircularProgress size={16} /> : null}
                  >
                    {isSaving ? 'Creating…' : 'Create Notice'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default NoticeManagementCreate;
